// Shared class/spec icon resolution for Planner-adjacent UIs (matches PlannerPage roster logic).

export type FieldOption = {
  id: number;
  key: string;
  label: string;
  metadata: any;
  image_url?: string | null;
};

export type FieldGroup = {
  id: number;
  key: string;
  label: string;
  multiSelect: boolean;
  isRequired: boolean;
  options: FieldOption[];
};

const ROLE_LABEL_MAP: Record<string, string> = {
  tank: "Tank",
  healer: "Healer",
  melee_dps: "Melee DPS",
  ranged_dps: "Ranged DPS",
};

export function resolveRoleLabels(c: any, fields: FieldGroup[] | null | undefined): string[] {
  const primaryRole = (c.primaryRole ?? c.primary_role ?? c.metadata?.primary_role ?? "") as string;
  const trimmedPrimary = primaryRole ? primaryRole.trim() : "";
  if (!fields || !Array.isArray(c.options)) {
    const metaLabels = c.metadata?.role_labels || c.roles;
    if (Array.isArray(metaLabels) && metaLabels.length > 0) return metaLabels;
    return trimmedPrimary ? [trimmedPrimary] : [];
  }
  const roleGroup = fields.find((g) => g.key === "role");
  if (!roleGroup) {
    const metaLabels = c.metadata?.role_labels || c.roles;
    if (Array.isArray(metaLabels) && metaLabels.length > 0) return metaLabels;
    return trimmedPrimary ? [trimmedPrimary] : [];
  }
  const rawOptions: any[] = c.options ?? [];
  let labels = rawOptions
    .filter((o: any) => o && Number(o.group_id) === Number(roleGroup.id))
    .map((o: any) => {
      const opt = roleGroup.options.find((fo: any) => Number(fo.id) === Number(o.option_id));
      return (opt?.label ?? "").trim();
    })
    .filter((lbl: string) => lbl.length > 0);
  if (!labels.length) {
    const metaLabels = c.metadata?.role_labels || c.roles;
    if (Array.isArray(metaLabels) && metaLabels.length > 0) return metaLabels;
    return trimmedPrimary ? [trimmedPrimary] : [];
  }
  return labels;
}

export function resolveSpecEntries(
  c: any,
  fields: FieldGroup[] | null | undefined
): { specLabel: string; roleLabel: string; specKey: string }[] {
  if (!fields || !Array.isArray(c.options)) {
    const meta = c.metadata || {};
    const specLabels: string[] = meta.spec_labels || [];
    const roleLabels: string[] = meta.role_labels || [];
    if (specLabels.length > 0) {
      return specLabels.map((sl: string, i: number) => ({
        specLabel: sl,
        roleLabel: roleLabels[i] || "DPS",
        specKey: (meta.spec_keys || [])[i] || sl.toLowerCase(),
      }));
    }
    const roles = resolveRoleLabels(c, fields);
    return roles.map((r: string) => ({ specLabel: r, roleLabel: r, specKey: r.toLowerCase().replace(/\s+/g, "_") }));
  }
  const specGroup = fields.find((g) => g.key === "spec");
  if (!specGroup) {
    const roles = resolveRoleLabels(c, fields);
    return roles.map((r: string) => ({ specLabel: r, roleLabel: r, specKey: r.toLowerCase().replace(/\s+/g, "_") }));
  }
  const rawOptions: any[] = c.options ?? [];
  const entries = rawOptions
    .filter((o: any) => o && Number(o.group_id) === Number(specGroup.id))
    .map((o: any) => {
      const opt = specGroup.options.find((fo: any) => Number(fo.id) === Number(o.option_id));
      if (!opt) return null;
      const specLabel = (opt.label ?? "").trim();
      const roleKey = (opt.metadata?.role_key ?? "").trim();
      const roleLabel = ROLE_LABEL_MAP[roleKey] || roleKey || "DPS";
      const specKey = opt.key ?? specLabel.toLowerCase().replace(/\s+/g, "_");
      return specLabel ? { specLabel, roleLabel, specKey } : null;
    })
    .filter((e: any): e is { specLabel: string; roleLabel: string; specKey: string } => e !== null);
  if (entries.length > 0) return entries;
  const roles = resolveRoleLabels(c, fields);
  return roles.map((r: string) => ({ specLabel: r, roleLabel: r, specKey: r.toLowerCase().replace(/\s+/g, "_") }));
}

export function getClassIconUrl(
  className: string | null | undefined,
  fields: FieldGroup[] | null | undefined
): string | null {
  if (!className || !fields) return null;
  const classGroup = fields.find((g) => g.key === "class");
  if (!classGroup) return null;
  const opt = classGroup.options.find((o) => o.label === className);
  return opt?.image_url || null;
}

export function getSpecIconUrl(
  specLabel: string | null | undefined,
  className: string | null | undefined,
  fields: FieldGroup[] | null | undefined
): string | null {
  if (!specLabel || !fields) return null;
  const specGroup = fields.find((g) => g.key === "spec");
  if (!specGroup) return null;
  const classKey = className?.toLowerCase().replace(/\s+/g, "") || "";
  const opt = specGroup.options.find(
    (o) => o.label === specLabel && o.metadata?.class_key === classKey
  );
  return opt?.image_url || null;
}

/** Same resolution as Planner detail roster / DraggableCard `classIconUrl`. */
export function resolveSeatIconUrl(
  charData: any | null | undefined,
  className: string | null | undefined,
  roleLabel: string | null | undefined,
  fields: FieldGroup[] | null | undefined
): string | null {
  if (!fields) return getClassIconUrl(className, fields);
  if (!charData) return getClassIconUrl(className, fields);
  const entries = resolveSpecEntries(charData, fields);
  const rl = (roleLabel || "").trim();
  const m = entries.find((e) => e.roleLabel === rl);
  const specUrl = getSpecIconUrl(m?.specLabel || entries[0]?.specLabel, className, fields);
  return specUrl || getClassIconUrl(className, fields);
}
